import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { AddQuestionModal } from "modules/ChecklistsDashboard/components";
import { ChecklistQuestionGroup, ParentQuestionGroups } from "modules/ChecklistsDashboard/components/EditChecklistModal/components";
import { hasTyreElement } from "modules/ChecklistsDashboard/util";
import { toISOString } from "utils/dateHelperFunctions";

import type { Checklist, Question, QuestionGroup } from "models";

type QuestionGroupsProps = {
  canEdit: boolean;
};

const FormHelper = createFormHelper<Checklist>();

export const QuestionGroups = ({ canEdit }: QuestionGroupsProps) => {
  const { t } = useTranslation();

  const [addQuestionGroupIdx, setAddQuestionGroupIdx] = useState<number | null>(null);

  const { setValue } = FormHelper.useFormContext();
  const questionGroups = FormHelper.useWatch("question_groups") ?? [];
  const parentChecklist = FormHelper.useWatch("parent_checklist");

  const updateQuestionGroups = (updatedQuestionGroups: QuestionGroup[]) => setValue("question_groups", updatedQuestionGroups, { shouldDirty: true });

  const handleAddGroup = () => {
    if (questionGroups.some(questionGroup => !questionGroup.questions?.length))
      return toast.error(t("v8_you_already_have_an_empty_group", "You already have an empty group"));

    const now = toISOString(new Date());
    updateQuestionGroups([...questionGroups, { id: -1, name: "", ordering: questionGroups.length, questions: [], created_on: now, updated_on: now }]);
  };

  const handleMoveGroup = (fromIdx: number, toIdx: number) =>
    updateQuestionGroups(questionGroups.with(fromIdx, { ...questionGroups[toIdx], ordering: fromIdx }).with(toIdx, { ...questionGroups[fromIdx], ordering: toIdx }));

  const handleRemoveGroup = (questionGroupIdx: number) =>
    updateQuestionGroups(questionGroups.toSpliced(questionGroupIdx, 1).map((questionGroup, qgIdx) => ({ ...questionGroup, ordering: qgIdx })));

  const handleChangeQuestions = (questionGroupIdx: number, questions: Question[]) =>
    updateQuestionGroups(questionGroups.with(questionGroupIdx, { ...questionGroups[questionGroupIdx], questions }));

  const handleAddQuestion = (questionGroupIdx: number) => {
    if (questionGroups[questionGroupIdx].questions?.some(hasTyreElement))
      return toast.error(t("v8_cannot_add_question_tyre_question_in_group", "Cannot add a new question because a question with a tyre element is present in this group"));

    setAddQuestionGroupIdx(questionGroupIdx);
  };

  const handleSelectQuestion = (question: Question) => {
    if (addQuestionGroupIdx === null) return;

    const questions = questionGroups[addQuestionGroupIdx].questions ?? [];
    handleChangeQuestions(addQuestionGroupIdx, [...questions, { ...question, ordering: questions.length }]);
  };

  return (
    <div className="QuestionGroups">
      {parentChecklist?.question_groups && <ParentQuestionGroups questionGroups={parentChecklist.question_groups} />}

      {questionGroups.map((questionGroup, qgIdx) => (
        <ChecklistQuestionGroup
          key={`question-group-${questionGroup.ordering}`}
          questionGroup={questionGroup}
          questionGroupIdx={qgIdx}
          isFirst={qgIdx === 0}
          isLast={qgIdx === questionGroups.length - 1}
          canEdit={canEdit}
          onMove={handleMoveGroup}
          onRemove={handleRemoveGroup}
          onAddQuestion={handleAddQuestion}
          onChangeQuestions={handleChangeQuestions}
        />
      ))}

      {canEdit && (
        <Button type="button" color="green" fluid className="add-group-button" onClick={handleAddGroup}>
          {t("v8_add_group", "Add group")}
        </Button>
      )}

      <AddQuestionModal
        isOpen={addQuestionGroupIdx !== null}
        questionGroup={addQuestionGroupIdx !== null ? questionGroups[addQuestionGroupIdx] : undefined}
        onSelectQuestion={handleSelectQuestion}
        onClose={() => setAddQuestionGroupIdx(null)}
      />
    </div>
  );
};
